import { AES, enc, lib, mode, pad, SHA256 } from 'crypto-js';

const ENCRYPTION_KEY = process.env.NEXT_PUBLIC_INVITATION_ENCRYPTION_KEY;

interface DecryptedData {
  email: string;
  team_id: string;
  region: string;
}

export const decryptInvitationData = (encryptedData: string): DecryptedData | null => {
  if (!ENCRYPTION_KEY || !encryptedData) {
    return null;
  }

  try {
    const hashedKey = SHA256(ENCRYPTION_KEY);
    const decodedData = enc.Base64.parse(encryptedData);
    const iv = lib.WordArray.create(decodedData.words.slice(0, 4));
    const encryptedContent = lib.WordArray.create(decodedData.words.slice(4));

    const decrypted = AES.decrypt(
      { ciphertext: encryptedContent } as unknown as lib.CipherParams,
      hashedKey,
      {
        iv,
        mode: mode.CBC,
        padding: pad.Pkcs7,
      }
    );

    const string = decrypted.toString(enc.Utf8);
    return JSON.parse(string) as DecryptedData;
  } catch {
    return null;
  }
};

export const getInvitationDataFromURL = (
  search?: string
): {
  invitedEmail: string | undefined;
  teamId: string | undefined;
  region: string | undefined;
} => {
  let urlSearch = search;
  if (!urlSearch && typeof window !== 'undefined') {
    urlSearch = window.location.search;
  }

  const urlParams = urlSearch ? new URLSearchParams(urlSearch) : undefined;

  if (!urlParams) {
    return {
      invitedEmail: undefined,
      teamId: undefined,
      region: undefined,
    };
  }

  // Check for encrypted data first
  const encryptedData = urlParams.get('q');
  if (encryptedData) {
    const decryptedData = decryptInvitationData(encryptedData);
    if (decryptedData) {
      return {
        invitedEmail: decryptedData.email,
        teamId: decryptedData.team_id,
        region: decryptedData.region,
      };
    } else {
      return {
        invitedEmail: undefined,
        teamId: undefined,
        region: undefined,
      };
    }
  }

  // Return regular query parameters for non-encrypted URLs
  return {
    invitedEmail: urlParams.get('email') ?? undefined,
    teamId: urlParams.get('team_id') ?? undefined,
    region: urlParams.get('region') ?? undefined,
  };
};
